import { Classes } from '@blueprintjs/core';
import { AuthSelectors, useTranslation } from '@web_opxp/features';
import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { formatDateToString } from '../../base/dateUtils';
import { ILegacySubscription, TSubscriptionID } from '../types';
import { canProductBeRestored } from '../utils';
import NonTerminatable from './NonTerminatable';
import RestoreButton from './RestoreButton';
import './SubscriptionListItem.scss';
import TerminateButton from './TerminateButton';

export interface ILegacySubscriptionStatusProps {
  subscription: ILegacySubscription;
  onTerminateSubscription: (id: TSubscriptionID) => void;
}

export default function LegacySubscriptionStatus({
  subscription,
  onTerminateSubscription,
}: Readonly<ILegacySubscriptionStatusProps>) {
  const { productId, id, autoRenewing, createdAt, expiresAt, nextCharge, isTerminable } = subscription;
  const { language, t } = useTranslation();
  const premiumExpiresAt = useSelector(AuthSelectors.getPremiumEndTime);
  const productTitle = t([
    `common.products:Current.${productId}`,
    `common.products:Legacy.${productId}`,
    'common.products:Legacy.unknown',
  ]);
  const fCreatedAt = formatDateToString(createdAt, language);
  const expirationDate = premiumExpiresAt ?? expiresAt;
  const fExpiresAt = formatDateToString(expirationDate, language);
  let fNextCharge = '-';
  let smallInfo: ReactNode = <small>({t('manage:Manage.Subscription.Terminated')})</small>;
  if (autoRenewing) {
    fNextCharge = formatDateToString(nextCharge, language);
    smallInfo = null;
  }
  const familyBadge = subscription.secondProductId ? (
    <small className={`${Classes.TAG} ${Classes.INTENT_SUCCESS} ${Classes.MINIMAL}`}>
      {t('manage:Manage.Subscription.FamilyPackage')}
    </small>
  ) : null;
  // subscription can be restored if its product id is included in the list from remote config
  const canBeRestored = canProductBeRestored(productId);
  const showRestoreButton = !autoRenewing && isTerminable && canBeRestored;

  const handleTerminate = () => onTerminateSubscription(id);

  return (
    <>
      <div className="row">
        <div className="col-3 col-m-2">
          <p className="label">{t('manage:Manage.Subscription.Title')}</p>
        </div>
        <div className="col-9 col-m-2">
          <p>
            {productTitle} {smallInfo} {familyBadge}
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col-3 col-m-2">
          <p className="label">{t('manage:Manage.Subscription.Started')}</p>
        </div>
        <div className="col-9 col-m-2">
          <p>{fCreatedAt}</p>
        </div>
      </div>
      <div className="row">
        <div className="col-3 col-m-2">
          <p className="label">{t('manage:Manage.Subscription.Expire')}</p>
        </div>
        <div className="col-9 col-m-2">
          <p>{fExpiresAt}</p>
        </div>
      </div>
      <div className="row">
        <div className="col-3 col-m-2">
          <p className="label">{t('manage:Manage.Subscription.NextCharge')}</p>
        </div>
        <div className="col-9 col-m-2">
          <p>{fNextCharge}</p>
        </div>
      </div>
      <NonTerminatable show={autoRenewing && !isTerminable} />
      <TerminateButton show={autoRenewing && isTerminable} onTerminate={handleTerminate} />
      <RestoreButton id={id} show={showRestoreButton} />
    </>
  );
}
